// Generated by OrchestKit Claude Plugin
// Created: 2026-09-24

/**
 * Calculs d'une vente — RG-002 à RG-004, RG-035, RG-078.
 *
 * Port direct de `mobile/lib/vente/calculs.dart` : mêmes règles, mêmes noms.
 * Fonctions PURES, sans appel réseau. Le serveur reste seul juge : un écart
 * entre les deux devient une anomalie à sa charge, jamais une correction
 * silencieuse ici.
 */

export interface LignePanier {
  produitId: string;
  designation: string;
  quantite: number;
  prixUnitaire: number;
  /** Remise sur la ligne, montant TOTAL et non unitaire. */
  remiseLigne: number;
}

export interface TotauxVente {
  sousTotal: number;
  remisePied: number;
  arrondi: number;
  total: number;
}

export function totalLigne(ligne: LignePanier): number {
  return ligne.quantite * ligne.prixUnitaire - ligne.remiseLigne;
}

/** Pas d'arrondi du total — RG-004. Les pièces de 1 et 2 FCFA ne circulent pas au Bénin. */
export const PAS_ARRONDI = 5;

/**
 * Totaux d'un panier — RG-002 à RG-004.
 *
 * L'arrondi porte sur le TOTAL, jamais sur les lignes : arrondir chaque ligne
 * accumulerait les écarts et rendrait le ticket incohérent avec la somme de
 * ses lignes.
 */
export function calculerTotaux(lignes: LignePanier[], remisePied = 0): TotauxVente {
  const sousTotal = lignes.reduce((somme, ligne) => somme + totalLigne(ligne), 0);
  const avantArrondi = sousTotal - remisePied;

  // Arrondi au plus proche : 1 002 devient 1 000, 1 003 devient 1 005.
  const total = Math.floor((avantArrondi + Math.floor(PAS_ARRONDI / 2)) / PAS_ARRONDI) * PAS_ARRONDI;

  return { sousTotal, remisePied, arrondi: total - avantArrondi, total };
}

/**
 * Remise MAXIMALE, lignes et pied cumulés — RG-078.
 *
 * La base est le montant AVANT toute remise, et le plafond porte sur le
 * cumul (lignes + pied), pas seulement sur la remise de pied.
 */
export function remiseMaximale(lignes: LignePanier[], plafondPct: number): number {
  const brut = lignes.reduce((somme, ligne) => somme + ligne.quantite * ligne.prixUnitaire, 0);
  return Math.floor((brut * plafondPct) / 100);
}

export function remiseCumulee(lignes: LignePanier[], remisePied: number): number {
  return lignes.reduce((somme, ligne) => somme + ligne.remiseLigne, 0) + remisePied;
}

/** Le seuil de quantité du canal est-il atteint — RG-035 ? */
export function seuilAtteint(quantite: number, quantiteMinimale: number): boolean {
  return quantite >= quantiteMinimale;
}

/** Reste à payer après les règlements saisis. */
export function resteAPayer(total: number, paiements: number[]): number {
  const encaisse = paiements.reduce((somme, montant) => somme + montant, 0);
  return total - encaisse;
}

/** Monnaie à rendre. Zéro si le montant reçu ne couvre pas le total. */
export function monnaieARendre(total: number, recu: number): number {
  const ecart = recu - total;
  return ecart > 0 ? ecart : 0;
}
